import React, { useContext } from "react";
import { Button, Card, Col, Form, Row, Tab } from "react-bootstrap";
import IContactInfo, { AuthContext, User } from "../../../../context/AuthContext";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup';
import { Switch } from "../../../../shared/components/Switch";
import { AuthService } from "../../../../authentication/service";
import { LOGIN_URL } from "../../../../helpers/api.routes";
import FormDropzone from "../../../../shared/dropzone";
import { ErrorToast, SuccessToast } from "../../../../shared/toasters/toasters";
import { useNavigate } from "react-router-dom";
import { multipartFormData } from "../../../../helpers/api.services";
import { useTranslation } from "react-i18next";

export interface UserProfile {
    email: string;
    firstName: string;
    contact: IContactInfo;
    lastName: string;

    profilePicture?: any
}

const schema = yup.object().shape({
    firstName: yup.string().required('formErrors.firstNameRequired'),
    lastName: yup.string().required('formErrors.lastNameRequired'),
    email: yup.string().email('Invalid email').required('formErrors.emailRequired'),
    contact: yup.object().shape({
        phone: yup.string().required('formErrors.phoneRequired'),
        city: yup.string().required('formErrors.cityRequired'),
        zip: yup.string().required('formErrors.cityRequired'),
        address: yup.string().required('formErrors.addressRequired'),
        addressSuffix: yup.string().required('formErrors.addressSuffixRequired'), // Added addressSuffix validation
    }),
    profilePicture: yup.mixed()
});

const PersonalInfo = ({ user, setUser }: { user: User, setUser: React.Dispatch<React.SetStateAction<User | null>>; }) => {
    const { t } = useTranslation(); // Initialize translation hook
    const navigate = useNavigate();

    const { control, handleSubmit, getValues, setValue, reset, formState: { errors } } = useForm<UserProfile>({
        resolver: yupResolver(schema),
        defaultValues: {
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            email: user.email || "",
          
            contact: {
                phone: user.contact?.phone || "",
                city: user.contact?.city || "",
                zip: user.contact?.zip || "",
                address: user.contact?.address || "",
                addressSuffix: user.contact?.addressSuffix || "", // Added addressSuffix default value
            },
            profilePicture: user?.profilePicture,
        }
    });

    const handleImageUpload = (files: any) => {
        setValue("profilePicture", files[0]);
    };

    const onSubmit = async (data: UserProfile) => {
        multipartFormData(
            data.profilePicture,
            LOGIN_URL + '/me',
            JSON.stringify(data),
            'put',
            'image'
        )
            .then((response) => {
                SuccessToast(t('user.profileUpdated'));
                setUser(response.data);
                navigate('/profile-overview');
                reset();
            })
            .catch((error) => ErrorToast(error));
    };

    return (
        <React.Fragment>
            <Tab.Pane eventKey="PersonalInfo" className="fade" id="user-set-information" role="tabpanel">
                <Card>
                    <Card.Header>
                        <h5>{t('user.personalInformation')}</h5>
                    </Card.Header>
                    <Card.Body>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <Row>
                                <Col xl={6}>
                                    <div className="mb-3">
                                        <label className="form-label">{t('user.firstName')}</label>
                                        <Controller
                                            name="firstName"
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                                                    placeholder={t('user.enterFirstName')}
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {errors.firstName && <div className="invalid-feedback">
                                            {t(errors.firstName.message as string)}
                                        </div>}
                                    </div>
                                </Col>
                                <Col xl={6}>
                                    <div className="mb-0">
                                        <label className="form-label">{t('user.lastName')}</label>
                                        <Controller
                                            name="lastName"
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                                                    placeholder={t('user.enterLastName')}
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {errors.lastName && <div className="invalid-feedback">
                                            {t(errors.lastName.message as string)}
                                        </div>}
                                    </div>
                                </Col>
                                <Col xl={6}>
                                    <div className="mb-3">
                                        <label className="form-label">{t('user.email')}</label>
                                        <Controller
                                            name="email"
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="email"
                                                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                                    placeholder={t('user.enterEmail')}
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {errors.email && <div className="invalid-feedback">
                                            {t(errors.email.message as string)}
                                        </div>}
                                    </div>
                                </Col>
                                <Col xl={6}>
                                    <div className="mb-0">
                                        <label className="form-label">{t('user.phone')}</label>
                                        <Controller
                                            name="contact.phone"
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.contact?.phone ? 'is-invalid' : ''}`}
                                                    placeholder={t('user.enterPhone')}
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {errors.contact?.phone && <div className="invalid-feedback">
                                            {t(errors.contact?.phone.message as string)}
                                        </div>}
                                    </div>
                                </Col>
                                <Col xl={6}>
                                    <div className="mb-3">
                                        <label className="form-label">{t('user.city')}</label>
                                        <Controller
                                            name="contact.city"
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.contact?.city ? 'is-invalid' : ''}`}
                                                    placeholder={t('user.enterCity')}
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {errors.contact?.city && <div className="invalid-feedback">
                                            {t(errors.contact?.city.message as string)}
                                        </div>}
                                    </div>
                                </Col>
                                <Col xl={6}>
                                    <div className="mb-0">
                                        <label className="form-label">{t('user.zipCode')}</label>
                                        <Controller
                                            name="contact.zip"
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.contact?.zip ? 'is-invalid' : ''}`}
                                                    placeholder={t('user.enterZipCode')}
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {errors.contact?.zip && <div className="invalid-feedback">
                                            {t(errors.contact?.zip.message as string)}
                                        </div>}
                                    </div>
                                </Col>
                                <Col xl={6}>
                                    <div className="mb-0">
                                        <label className="form-label">{t('user.address')}</label>
                                        <Controller
                                            name="contact.address"
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.contact?.address ? 'is-invalid' : ''}`}
                                                    placeholder={t('user.enterAddress')}
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {errors.contact?.address && <div className="invalid-feedback">
                                            {t(errors.contact?.address.message as string)}
                                        </div>}
                                    </div>
                                </Col>
                                <Col xl={6}>
                                    <div className="mb-3">
                                        <label className="form-label">{t('user.addressSuffix')}</label>
                                        <Controller
                                            name="contact.addressSuffix"
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.contact?.addressSuffix ? 'is-invalid' : ''}`}
                                                    placeholder={t('user.enterAddressSuffix')}
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {errors.contact?.addressSuffix && <div className="invalid-feedback">
                                            {t(errors.contact?.addressSuffix.message as string)}
                                        </div>}
                                    </div>
                                </Col>
                           
                                <Col sm={6} className="mt-3">
                                    <label className="form-label">{t('user.image')}</label>
                                    <FormDropzone onFileUpload={handleImageUpload} existingFiles={getValues("profilePicture") ? [getValues("profilePicture")] : undefined} />
                                    {errors.profilePicture && <div className="invalid-feedback d-block">{t('user.imageRequired')}</div>}
                                </Col>
                            </Row>
                            <div className="text-end btn-page">
                                <Button variant="outline-secondary" onClick={() => navigate('/profile-overview')}>
                                    {t('user.cancel')}
                                </Button>
                                <Button type="submit" variant="primary">
                                    {t('user.updateProfile')}
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </Tab.Pane>
        </React.Fragment>
    );
};

export default PersonalInfo;